// Rewrites a stored image URL to a downscaled thumbnail served by the API (see
// apps/api/src/services/thumbnails.js). Originals stay untouched in storage; anything that isn't
// a recognised upload URL comes back unchanged, so this is always safe to call.

// Must match the API's allowed widths so a request lands on a cached variant instead of being snapped.
const THUMB_WIDTHS = [96, 160, 240, 320, 480, 640, 800, 960, 1280, 1600];
const MAX_DPR = 3;
// Bump to invalidate every cached thumbnail URL (browsers and Cloudflare), e.g. after a bad response was cached.
const THUMB_URL_VERSION = 2;

/** Width in device pixels needed to show an image `cssWidth` px wide sharply on this screen. */
function deviceWidth(cssWidth: number): number {
  const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
  const wanted = Math.ceil(cssWidth * Math.min(MAX_DPR, Math.max(1, dpr)));
  return THUMB_WIDTHS.find((w) => w >= wanted) ?? THUMB_WIDTHS[THUMB_WIDTHS.length - 1];
}

/**
 * `cssWidth` is how wide the image is drawn, in CSS pixels (the widest it gets, not a guess at density);
 * the device pixel ratio is applied here.
 */
export function thumbUrl(url: string | null | undefined, cssWidth: number): string {
  const u = String(url || '');
  if (!u) return '';
  const w = deviceWidth(cssWidth);
  // Public uploads (avatars, galleries, favourites): /files/<bucket>/uploads/<user>/<file>
  const pub = u.match(/^\/files\/[^/]+\/(uploads\/[^?#]+)$/);
  if (pub) return `/api/files/thumb/${pub[1]}?w=${w}&v=${THUMB_URL_VERSION}`;
  // Private chat attachments, served (and permission-checked) by the API
  const priv = u.match(/^(\/api\/files\/message\/[^/?#]+)$/);
  if (priv) return `${priv[1]}?w=${w}&v=${THUMB_URL_VERSION}`;
  return u;
}
